import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  useAcceptInternalInvitation,
  useVerifyInvite,
} from "@/hooks/useInternalUsers";
import { PASSWORD_HINT, passwordField } from "@/lib/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Shield, Sparkles, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { z } from "zod";

const acceptInternalInvitationSchema = z
  .object({
    name: z.string().optional(),
    password: passwordField(),
    confirmPassword: z.string(),
    bio: z.string().optional(),
    avatar: z.instanceof(File).optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type AcceptInternalInvitationFormData = z.infer<
  typeof acceptInternalInvitationSchema
>;

export default function AcceptInternalInvitation() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const {
    data: invitationData,
    isLoading: isLoadingInvitation,
    error: invitationError,
  } = useVerifyInvite(token);

  const acceptInvitationMutation = useAcceptInternalInvitation();

  const form = useForm<AcceptInternalInvitationFormData>({
    resolver: zodResolver(acceptInternalInvitationSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
      name: "",
      bio: "",
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  useEffect(() => {
    if (!token) {
      setLocation("/");
    }
  }, [token, setLocation]);

  useEffect(() => {
    if (invitationError) {
      const errorMessage =
        invitationError instanceof Error
          ? invitationError.message
          : "Invalid or expired invitation token";

      toast({
        title: "Invalid invitation",
        description:
          errorMessage.includes("expired") || errorMessage.includes("Invalid")
            ? "This invitation link is no longer valid. Please contact support."
            : errorMessage,
        variant: "destructive",
      });
      setLocation("/");
    }
  }, [invitationError, toast, setLocation]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("avatar", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: AcceptInternalInvitationFormData) => {
    if (!invitationData || !token) {
      toast({
        title: "Error",
        description: "Invalid invitation data",
        variant: "destructive",
      });
      return;
    }

    acceptInvitationMutation.mutate(
      {
        name: data.name || invitationData.invitation.name,
        email: invitationData.invitation.email,
        password: data.password,
        bio: data.bio,
        role: invitationData.invitation.role as "admin" | "coach_manager",
        token: token,
        avatar: data.avatar,
      },
      {
        onSuccess: () => {
          toast({
            title: "Invitation accepted successfully",
            description: "Your account has been created. You can now sign in.",
          });
          setLocation("/login");
        },
        onError: (error) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to accept invitation";
          toast({
            title: "Error accepting invitation",
            description: errorMessage,
            variant: "destructive",
          });
        },
      }
    );
  };

  if (isLoadingInvitation) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary animate-pulse" />
              </div>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Validating invitation...
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (!invitationData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Accept Internal User Invitation
          </h1>
          <p className="text-sm text-muted-foreground">
            Complete your profile to join the Evooluir platform
          </p>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">Set up your account</CardTitle>
            <CardDescription>
              Create your password and complete your profile information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          data-testid="input-name"
                          {...field}
                          defaultValue={invitationData.invitation.name || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      value={invitationData.invitation.email}
                      readOnly
                      disabled
                      className="bg-muted cursor-not-allowed"
                      data-testid="input-email"
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    This email is associated with your invitation
                  </p>
                </FormItem>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <div className="relative flex items-center">
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              className="pr-10"
                              data-testid="input-password"
                              {...field}
                            />
                            <button
                              type="button"
                              className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                              data-testid="button-toggle-password"
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {PASSWORD_HINT}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <div className="relative flex items-center">
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Confirm your password"
                              className="pr-10"
                              data-testid="input-confirm-password"
                              {...field}
                            />
                            <button
                              type="button"
                              className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              data-testid="button-toggle-confirm-password"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="avatar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Profile Photo (Optional)</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex items-center gap-4">
                            <Input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarChange}
                              className="hidden"
                              id="avatar-upload"
                              data-testid="input-avatar"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              className="flex items-center gap-2"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <Upload className="h-4 w-4" />
                              Upload Photo
                            </Button>
                            {avatarPreview && (
                              <img
                                src={avatarPreview}
                                alt="Avatar preview"
                                className="w-16 h-16 rounded-full object-cover"
                              />
                            )}
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us about yourself..."
                          rows={3}
                          data-testid="input-bio"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={acceptInvitationMutation.isPending}
                  data-testid="button-submit"
                >
                  {acceptInvitationMutation.isPending
                    ? "Creating account..."
                    : "Accept Invitation"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Shield className="h-3 w-3" />
          <span>Protected by enterprise-grade security</span>
        </div>
      </div>
    </div>
  );
}
